import axios from 'axios';
import { API_BASE_URL } from '../utils/config';
import { getToken, getRefreshToken, setToken, setRefreshToken, removeTokens } from '../utils/token';
import toast from 'react-hot-toast';
import { getBackendErrorKey } from '../utils/errorMapper';
import i18n from '../i18n/config';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - добавляем токен
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - обрабатываем ошибки и обновляем токен
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Если ошибка 401 и мы еще не пробовали обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = getRefreshToken();
      
      if (refreshToken) {
        try {
          // Пытаемся обновить токен
          const response = await axios.post(`${API_BASE_URL}/api/v1/auth/token/refresh`, {
            refreshToken
          });
          
          const { accessToken, refreshToken: newRefreshToken } = response.data;
          
          setToken(accessToken);
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
          }
          
          // Повторяем оригинальный запрос с новым токеном
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Если не удалось обновить токен - разлогиниваемся
          removeTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // Нет refresh токена - разлогиниваемся
        removeTokens();
        window.location.href = '/login';
      }
    }
    
    // Обработка других ошибок
    if (error.response) {
      // Получаем локализованный ключ ошибки
      const errorKey = getBackendErrorKey(error);
      const message = i18n.t(errorKey);
      
      // Показываем красивое toast уведомление
      toast.error(message, {
        duration: 4000,
        style: {
          background: '#EF4444',
          color: '#fff',
          borderRadius: '8px',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#EF4444',
        },
      });
    } else if (error.request) {
      // Ошибка сети - нет ответа от сервера
      const message = i18n.t('common.errors.networkError');
      toast.error(message, {
        duration: 4000,
        style: {
          background: '#F59E0B',
          color: '#fff',
          borderRadius: '8px',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#F59E0B',
        },
      });
    } else {
      // Ошибка конфигурации запроса
      const message = i18n.t('common.errors.unknown');
      toast.error(message, {
        duration: 4000,
        style: {
          background: '#6B7280',
          color: '#fff',
          borderRadius: '8px',
          padding: '12px 16px',
          fontSize: '14px',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#fff',
          secondary: '#6B7280',
        },
      });
    }
    
    return Promise.reject(error);
  }
);

export default api;
